import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { SidebarMode } from './MenuItems';
import { colorOptions, colorSkinOptions, colorEyesOptions, colorHairOptions } from './SpinnerContext';

// Interface for sidebar context
interface Participant {
  id: string;
  name: string;
}

interface SidebarContextType {
  isCollapsed: boolean;
  setIsCollapsed: (value: boolean) => void;
  currentMode: SidebarMode;
  setCurrentMode: (mode: SidebarMode) => void;
  isSidebarLocked: boolean;
  setIsSidebarLocked: (locked: boolean) => void;
  colorCount: number;
  setColorCount: (n: number) => void;
  colorType: 'random' | 'skin' | 'eyes' | 'hair';
  setColorType: (type: 'random' | 'skin' | 'eyes' | 'hair') => void;
  numberSelection: number;
  setNumberSelection: (n: number) => void;
  randomNumberSelection: number;
  setRandomNumberSelection: (n: number) => void;
  minRange: number;
  setMinRange: (n: number) => void;
  maxRange: number;
  setMaxRange: (n: number) => void;
  numberMode: 'integer' | 'decimal' | 'both';
  setNumberMode: (m: 'integer' | 'decimal' | 'both') => void;
  numbers: Participant[];
  randomNumbers: Participant[];
  colors: Participant[];
  binaryList: string[];
  setBinaryList: (list: string[]) => void;
  removeNumber: (id: string) => void;
  removeRandomNumber: (id: string) => void;
  removeColor: (id: string) => void;
}

// Create the context
const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// Provider component
export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [currentMode, setCurrentMode] = useState<SidebarMode>('participants');
  const [isSidebarLocked, setIsSidebarLocked] = useState(false);

  const [colorCount, setColorCount] = useState(6);
  const [colorType, setColorType] = useState<'random' | 'skin' | 'eyes'>('random');
  const [numberSelection, setNumberSelection] = useState(6);
  const [randomNumberSelection, setRandomNumberSelection] = useState(6);
  const [minRange, setMinRange] = useState(1);
  const [maxRange, setMaxRange] = useState(100);
  const [numberMode, setNumberMode] = useState<'integer' | 'decimal' | 'both'>('integer');

  // Derived lists
  const [numbers, setNumbers] = useState<Participant[]>(Array.from({ length: 6 }, (_, i) => ({ id: `${i + 1}`, name: `${i + 1}` })));
  const [randomNumbers, setRandomNumbers] = useState<Participant[]>(Array.from({ length: 6 }, (_, i) => {
    const value = Math.floor(Math.random() * 1000);
    return { id: `${value}`, name: `${value}` };
  }));
  const [colors, setColors] = useState<Participant[]>(colorOptions.slice(0, 6).map(c => ({ id: c.name, name: c.name })));
  const [binaryList, setBinaryList] = useState<string[]>(['Yes', 'No']);

  // Regenerate numbers when numberSelection changes
  useEffect(() => {
    setNumbers(Array.from({ length: numberSelection }, (_, i) => ({ id: `${i + 1}`, name: `${i + 1}` })));
  }, [numberSelection]);

  // Regenerate randomNumbers when randomNumberSelection, minRange, maxRange, or numberMode changes
  useEffect(() => {
    const min = Math.max(Number(minRange), -999999999);
    const max = Math.min(Number(maxRange), 999999999);
    if (isNaN(min) || isNaN(max) || min > max) {
      setRandomNumbers([]);
      return;
    }
    setRandomNumbers(Array.from({ length: randomNumberSelection }, (_, i) => {
      let value: number;
      if (numberMode === 'integer') {
        value = Math.round(Math.random() * (max - min) + min);
      } else if (numberMode === 'decimal') {
        value = Math.random() * (max - min) + min;
        value = Math.round(value * 100) / 100;
      } else {
        value = Math.random() > 0.5
          ? Math.round(Math.random() * (max - min) + min)
          : Math.round((Math.random() * (max - min) + min) * 100) / 100;
      }
      return { id: `${i + 1}-${value}`, name: value.toString() };
    }));
  }, [randomNumberSelection, minRange, maxRange, numberMode]);

  // Regenerate colors when colorCount or colorType changes
  useEffect(() => {
    let selectedColorOptions;
    switch (colorType) {
      case 'skin':
        selectedColorOptions = colorSkinOptions;
        break;
      case 'eyes':
        selectedColorOptions = colorEyesOptions;
        break;
      case 'hair':
        selectedColorOptions = colorHairOptions;
        break;
      default:
        selectedColorOptions = colorOptions;
    }
    setColors(selectedColorOptions.slice(0, colorCount).map(c => ({ id: c.name, name: c.name })));
  }, [colorCount, colorType]);

  const removeNumber = (id: string) => setNumbers(prev => prev.filter(n => n.id !== id));
  const removeRandomNumber = (id: string) => setRandomNumbers(prev => prev.filter(n => n.id !== id));
  const removeColor = (id: string) => setColors(prev => prev.filter(c => c.id !== id));

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        setIsCollapsed,
        currentMode,
        setCurrentMode,
        isSidebarLocked,
        setIsSidebarLocked,
        colorCount, setColorCount,
        colorType, setColorType,
        numberSelection, setNumberSelection,
        randomNumberSelection, setRandomNumberSelection,
        minRange, setMinRange,
        maxRange, setMaxRange,
        numberMode, setNumberMode,
        numbers,
        randomNumbers,
        colors,
        binaryList,
        setBinaryList,
        removeNumber,
        removeRandomNumber,
        removeColor,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

// Custom hook to use the sidebar context
export const useSidebar = (): SidebarContextType => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export default SidebarProvider; 